import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ExternalLink, ImageOff, X } from "lucide-react";
import { KIND_META, CONFIDENCE_META } from "./efConsts";

/**
 * MobileBottomSheet — mobile-native bottom sheet for entity details.
 *
 * Slides up from the bottom, has a drag handle, drag-down-to-dismiss,
 * tap-backdrop to dismiss, body scroll lock while open.
 *
 * Used only below the `lg` breakpoint; desktop uses InlineDetailPanel.
 */
export const MobileBottomSheet = ({ entity, sources = [], onClose, allEdges = [] }) => {
  // Lock body scroll while open
  useEffect(() => {
    if (entity) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [entity]);

  return (
    <AnimatePresence>
      {entity && (
        <>
          {/* Backdrop */}
          <motion.button
            type="button"
            aria-label="Close detail sheet"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-black/65 backdrop-blur-[2px] lg:hidden"
            data-testid="ef-bottom-sheet-backdrop"
          />
          {/* Sheet */}
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 32, stiffness: 340 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 130 || info.velocity.y > 400) onClose?.();
            }}
            className="fixed left-0 right-0 bottom-0 z-50 lg:hidden bg-[#0F0F0F] border-t border-[#D92D20]/40 max-h-[88vh] flex flex-col rounded-t-xl shadow-[0_-12px_36px_rgba(0,0,0,0.7)]"
            data-testid="ef-bottom-sheet"
          >
            <SheetBody entity={entity} sources={sources} edges={allEdges} onClose={onClose} />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const SheetBody = ({ entity, sources, edges, onClose }) => {
  const meta = KIND_META[entity.kind] || {};
  const cm = CONFIDENCE_META[entity.confidence_level] || {};
  const conn = edges.filter(
    (e) => e.src_entity_id === entity.id || e.dst_entity_id === entity.id,
  ).length;
  const refIds = new Set(entity.source_refs || []);
  const ownSources = sources.filter((s) => refIds.has(s.id));

  return (
    <>
      {/* Drag handle */}
      <div className="pt-2 pb-1 flex flex-col items-center cursor-grab active:cursor-grabbing">
        <div className="w-10 h-1 bg-[#454038] rounded-full" data-testid="ef-bottom-sheet-handle" />
      </div>

      {/* Header */}
      <div className="px-4 pt-1 pb-3 border-b border-[#2D2A26] flex items-center justify-between gap-2">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20]">
          // SELECTED SUBJECT
        </div>
        <div className="flex items-center gap-2">
          {entity.confidence_level && (
            <span
              style={{ color: cm.color, borderColor: `${cm.color}80` }}
              className="font-mono text-[8px] uppercase tracking-[0.25em] border px-1.5 py-[1px]"
            >
              {cm.label}
            </span>
          )}
          <button
            onClick={onClose}
            data-testid="ef-bottom-sheet-close"
            className="text-[#A39E93] hover:text-[#E4DFD3]"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 overscroll-contain">
        {/* Hero row: photo + name */}
        <div className="flex gap-3">
          <div className="w-24 h-28 border border-[#2D2A26] bg-[#0a0907] relative overflow-hidden shrink-0">
            {(entity.photo_url || entity.thumbnail_url || entity.image_url) ? (
              <img
                src={entity.photo_url || entity.thumbnail_url || entity.image_url}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-[#454038]">
                <ImageOff size={28} strokeWidth={1.2} />
              </div>
            )}
            {entity.is_anchor && (
              <div className="absolute top-1 left-1 font-mono text-[7px] uppercase tracking-[0.3em] text-[#0F0F0F] bg-[#D92D20] px-1 py-[1px]">
                ANCHOR
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-0.5">
              {meta.label || (entity.kind || "—").toUpperCase()}
            </div>
            <h2 className="font-display text-xl text-[#E4DFD3] leading-tight">{entity.name}</h2>
            {entity.occupation_or_role && (
              <div className="text-[12px] text-[#A39E93] mt-1">{entity.occupation_or_role}</div>
            )}
          </div>
        </div>

        {(entity.summary || entity.connection_summary_to_epstein) && (
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] mb-1">// OVERVIEW</div>
            <p className="text-[12px] text-[#CBB892] leading-relaxed">
              {entity.summary || entity.connection_summary_to_epstein}
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-1.5">
          {entity.category_label && (
            <span className="font-mono text-[9px] uppercase tracking-[0.25em] border border-[#454038] text-[#A39E93] px-2 py-1">
              {entity.category_label}
            </span>
          )}
          {entity.nationality && (
            <span className="font-mono text-[9px] uppercase tracking-[0.25em] border border-[#454038] text-[#A39E93] px-2 py-1">
              {entity.nationality}
            </span>
          )}
          <span className="font-mono text-[9px] uppercase tracking-[0.25em] border border-[#454038] text-[#A39E93] px-2 py-1">
            {conn} CONN
          </span>
          <span className="font-mono text-[9px] uppercase tracking-[0.25em] border border-[#454038] text-[#A39E93] px-2 py-1">
            {(entity.source_refs || []).length} SRC
          </span>
        </div>

        {(entity.first_known_connection_date || entity.last_known_connection_date) && (
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] mb-1">
              // KNOWN CONNECTION WINDOW
            </div>
            <div className="font-mono text-[11px] text-[#CBB892]">
              {entity.first_known_connection_date || "?"} → {entity.last_known_connection_date || "?"}
            </div>
          </div>
        )}

        {entity.legal_note && (
          <div className="border-l-2 border-[#E0A526] pl-2 text-[11px] italic text-[#CBB892]">
            ⚠ {entity.legal_note}
          </div>
        )}

        {ownSources.length > 0 && (
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] mb-1.5">
              // SOURCES ({ownSources.length})
            </div>
            <ul className="space-y-1">
              {ownSources.slice(0, 8).map((s) => (
                <li key={s.id}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block text-[11px] text-[#A39E93] hover:text-[#E4DFD3] border-b border-[#2D2A26]/60 py-1"
                  >
                    <div className="flex items-start gap-1.5">
                      <ExternalLink size={10} className="mt-0.5 text-[#5a544a] shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate">{s.title || s.url}</div>
                        {s.domain && (
                          <div className="font-mono text-[9px] text-[#5a544a] uppercase tracking-[0.2em]">
                            {s.domain}
                          </div>
                        )}
                      </div>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="p-3 border-t border-[#2D2A26]">
        <Link
          to={`/epstein-files/entity/${entity.slug}`}
          onClick={onClose}
          data-testid="ef-bottom-sheet-view-all"
          className="flex items-center justify-center gap-2 w-full bg-[#D92D20] hover:bg-[#B92318] text-white font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2.5"
        >
          View All Connections <ArrowRight size={12} />
        </Link>
      </div>
    </>
  );
};

export default MobileBottomSheet;
